import OrganClock from "@/components/OrganClock";
import { CLOCK, CLOCK_DICAS, RELOGIO_PAGINA_7 } from "@/data/clock";
import { elementColor } from "@/data/theme";
import { PageHeader, SafetyNote, SourceTag } from "@/components/common";
import { Link } from "react-router-dom";
import { ArrowRight, Clock as ClockIcon, Sparkles, Moon } from "lucide-react";

export default function Clock() {
  return (
    <div className="space-y-12">
      {/* PÁGINA FÍSICA 7 / IMPRESSA 5: O RELÓGIO DOS ÓRGÃOS */}
      <section className="space-y-8" data-testid="section-relogio-pagina-7">
        <PageHeader
          eyebrow="Ritmo biológico da MTC"
          title={RELOGIO_PAGINA_7.titulo}
          subtitle={`${RELOGIO_PAGINA_7.subtitulo} ${RELOGIO_PAGINA_7.subtituloComplemento}`}
        >
          <div className="mt-3">
            <SourceTag source={RELOGIO_PAGINA_7.source} />
          </div>
        </PageHeader>

        <div className="grid gap-8 lg:grid-cols-12 items-center">
          {/* Textos auxiliares e bloco Como Usar da Página 7 */}
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
              <p className="font-serif text-lg text-foreground font-medium">
                {RELOGIO_PAGINA_7.intro1}
              </p>
              <p>{RELOGIO_PAGINA_7.intro2}</p>
            </div>

            {/* Bloco: Como usar este relógio */}
            <div className="rounded-3xl border border-border bg-card p-6 sm:p-7 space-y-4" data-testid="box-como-usar-relogio">
              <h3 className="font-serif text-lg font-semibold text-foreground flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-gold" />
                {RELOGIO_PAGINA_7.comoUsar.titulo}
              </h3>
              <div className="space-y-3.5">
                {RELOGIO_PAGINA_7.comoUsar.passos.map((passo, i) => {
                  const Icons = [ClockIcon, Sparkles, Moon];
                  const StepIcon = Icons[i] || ClockIcon;
                  return (
                    <div key={i} className="flex items-start gap-3 rounded-2xl bg-secondary/40 border border-border/60 p-3.5">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gold/15 text-gold text-xs mt-0.5">
                        <StepIcon className="h-4 w-4" />
                      </span>
                      <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-medium">
                        {passo}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Relógio Interativo existente */}
          <div className="lg:col-span-7 flex justify-center">
            <div className="w-full max-w-md">
              <OrganClock size={360} />
            </div>
          </div>
        </div>

        {/* Citação da Página 7 */}
        <div className="rounded-2xl border border-gold/30 bg-gold/5 p-6 text-center">
          <p className="font-serif text-lg sm:text-xl font-medium italic text-foreground">
            “{RELOGIO_PAGINA_7.citacao}”
          </p>
        </div>
      </section>

      {/* PÁGINA FÍSICA 8 / IMPRESSA 6: O CICLO COMPLETO DE 24 HORAS */}
      <section className="space-y-8 border-t border-border pt-10" data-testid="section-ciclo-24h">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl sm:text-3xl font-serif font-medium">O ciclo completo</h2>
          <SourceTag source={{ pdfPage: 8, printedPage: "6" }} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CLOCK.map((s) => {
            const c = elementColor(s.elemento);
            return (
              <Link
                key={s.orgaoId}
                to={`/orgaos/${s.orgaoId}`}
                data-testid={`clock-list-${s.orgaoId}`}
                className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-gold/40"
              >
                <div className="flex items-center justify-between">
                  <span className="font-serif text-lg font-semibold" style={{ color: c.primary }}>{s.periodo}</span>
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: c.primary }} />
                </div>
                <p className="mt-1 font-medium text-foreground">{s.orgao}</p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.descricao}</p>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: c.text }}>{s.cuidado}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-gold group-hover:gap-2 transition-all">
                  Abrir ficha <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>

        <div className="rounded-3xl border border-border bg-secondary/50 p-7">
          <h3 className="font-serif text-xl font-medium">Dicas para alinhar-se ao relógio</h3>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {CLOCK_DICAS.map((d, i) => (
              <li key={i} className="flex gap-2 text-sm text-foreground"><span className="text-gold">•</span> {d}</li>
            ))}
          </ul>
        </div>

        <SafetyNote>
          Viver em sintonia com o relógio do corpo é viver em equilíbrio. Este conteúdo é educativo e não substitui avaliação profissional.
        </SafetyNote>
      </section>
    </div>
  );
}
